/**
 * @fileoverview React hook for Reddit Connect operations.
 * @module reddit-connect/hooks/useRedditConnect
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createRedditConnectService } from '../services/RedditConnectService';
import type { RedditConnectionStatus } from '../types';

const OAUTH_SESSION_KEY = 'reddit_oauth_in_progress';

function isAbortError(error: unknown): boolean {
  return error instanceof Error && error.name === 'AbortError';
}

export interface UseRedditConnectResult {
  status: RedditConnectionStatus | null;
  isConnected: boolean;
  loading: boolean;
  actionLoading: boolean;
  error: string | null;
  refreshStatus: () => Promise<void>;
  connect: () => Promise<void>;
  disconnect: () => Promise<void>;
}

export function useRedditConnect(projectId: string): UseRedditConnectResult {
  const [status, setStatus] = useState<RedditConnectionStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const statusAbortRef = useRef<AbortController | null>(null);
  const processedCallbackRef = useRef(false);
  const mountedRef = useRef(true);

  const service = useMemo(() => createRedditConnectService(), []);

  const refreshStatus = useCallback(async () => {
    if (!projectId) return;

    statusAbortRef.current?.abort();
    statusAbortRef.current = new AbortController();

    try {
      setLoading(true);
      setError(null);
      const nextStatus = await service.getStatus(projectId, statusAbortRef.current.signal);
      if (mountedRef.current) {
        setStatus(nextStatus);
      }
    } catch (err) {
      if (isAbortError(err)) return;
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to load Reddit connection status');
        setStatus({ connected: false });
      }
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [projectId, service]);

  const connect = useCallback(async () => {
    if (!projectId) return;

    try {
      setActionLoading(true);
      setError(null);
      const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
      const oauthUrl = await service.getOAuthUrl(projectId, returnTo);
      sessionStorage.setItem(OAUTH_SESSION_KEY, projectId);
      window.location.href = oauthUrl;
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to connect Reddit account');
        setActionLoading(false);
      }
    }
  }, [projectId, service]);

  const disconnect = useCallback(async () => {
    if (!projectId) return;

    try {
      setActionLoading(true);
      setError(null);
      const nextStatus = await service.disconnect(projectId);
      if (mountedRef.current) {
        setStatus(nextStatus);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to disconnect Reddit account');
      }
      throw err;
    } finally {
      if (mountedRef.current) {
        setActionLoading(false);
      }
    }
  }, [projectId, service]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      statusAbortRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    refreshStatus();
  }, [refreshStatus]);

  useEffect(() => {
    if (processedCallbackRef.current || !projectId) return;

    const params = new URLSearchParams(window.location.search);
    const callbackState = params.get('reddit');
    const callbackError = params.get('error');
    const oauthProjectId = sessionStorage.getItem(OAUTH_SESSION_KEY);

    if (!callbackState) return;
    if (oauthProjectId && oauthProjectId !== projectId) return;

    if (callbackState === 'connected') {
      processedCallbackRef.current = true;
      sessionStorage.removeItem(OAUTH_SESSION_KEY);
      window.history.replaceState({}, '', window.location.pathname);
      refreshStatus();
      return;
    }

    if (callbackState === 'error' || callbackError) {
      processedCallbackRef.current = true;
      sessionStorage.removeItem(OAUTH_SESSION_KEY);
      window.history.replaceState({}, '', window.location.pathname);
      setError(callbackError ? decodeURIComponent(callbackError) : 'Reddit OAuth failed');
    }
  }, [projectId, refreshStatus]);

  return {
    status,
    isConnected: status?.connected ?? false,
    loading,
    actionLoading,
    error,
    refreshStatus,
    connect,
    disconnect,
  };
}

export default useRedditConnect;

